import { useEffect, useState } from "react";
import { api } from "../../../lib/api";
import { toast } from "sonner";
import { FileText, DownloadSimple, ArrowClockwise, CircleNotch } from "@phosphor-icons/react";

const TYPE_LABEL = { PI: "Proforma Invoice", PR: "Payment Receipt", TI: "Tax Invoice", RV: "Refund Voucher" };
const SUB_LABEL = { MAIN: "MAIN", DELTA: "DELTA", SUCCESS: "", CANCELLED: "Cancelled" };
const STATE = {
  AVAILABLE: { t: "Available", c: "text-emerald-600" },
  GENERATING: { t: "Generating", c: "text-amber-600" },
  GENERATION_FAILED: { t: "Generation failed", c: "text-red-600" },
};

export function FinanceDocumentsPanel({ d, profile }) {
  const s = d?.shipment || d;
  const shipmentId = s?.id;
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const canRetry = ["SUPER_ADMIN", "ACCOUNTS"].includes(profile?.role);

  async function load() {
    try {
      const { data } = await api.get(`/finance-documents/${shipmentId}`);
      setDocs(data.documents || []);
    } catch {
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (shipmentId) load(); }, [shipmentId]);

  async function download(doc) {
    setBusy(doc.id);
    try {
      const res = await api.get(`/finance-documents/${shipmentId}/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch {
      toast.error("This document could not be opened. Please try again.");
    } finally {
      setBusy(null);
    }
  }

  async function retry(doc) {
    setBusy(doc.id);
    try {
      await api.post(`/finance-documents/${shipmentId}/${doc.id}/retry`);
      toast.success("Retrying document generation…");
      await load();
    } catch {
      toast.error("Could not retry right now. Please try again shortly.");
    } finally {
      setBusy(null);
    }
  }

  if (loading) return null;
  if (!docs.length) return null;

  // Group multi-RV under one refund action for readability.
  const rvGroups = {};
  const flat = [];
  for (const doc of docs) {
    if (doc.type === "RV" && doc.refund_group_id) {
      (rvGroups[doc.refund_group_id] ||= []).push(doc);
    } else {
      flat.push(doc);
    }
  }

  const Row = ({ doc }) => {
    const st = STATE[doc.status] || STATE.GENERATING;
    const sub = SUB_LABEL[doc.sub_type] ? ` · ${SUB_LABEL[doc.sub_type]}` : "";
    return (
      <div className="flex items-center justify-between py-2 border-b border-black/5 last:border-0"
           data-testid={`finance-doc-${doc.type}-${doc.id}`}>
        <div className="flex items-center gap-3 min-w-0">
          <FileText size={18} className="text-neutral-500 shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{TYPE_LABEL[doc.type] || doc.type}{sub}</div>
            <div className="text-xs text-neutral-500">{doc.number} · {doc.date || "—"}</div>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className={`text-xs font-medium ${st.c}`} data-testid={`finance-doc-status-${doc.id}`}>{st.t}</span>
          {doc.status === "AVAILABLE" && (
            <button onClick={() => download(doc)} disabled={busy === doc.id}
                    className="inline-flex items-center gap-1 text-xs font-medium text-neutral-800 hover:text-black"
                    data-testid={`finance-doc-download-${doc.id}`}>
              {busy === doc.id ? <CircleNotch size={14} className="animate-spin" /> : <DownloadSimple size={14} />} Download
            </button>
          )}
          {doc.status === "GENERATION_FAILED" && canRetry && (
            <button onClick={() => retry(doc)} disabled={busy === doc.id}
                    className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-900"
                    data-testid={`finance-doc-retry-${doc.id}`}>
              {busy === doc.id ? <CircleNotch size={14} className="animate-spin" /> : <ArrowClockwise size={14} />} Retry
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="rounded-xl border border-black/10 bg-white p-4" data-testid="finance-documents-panel">
      <div className="text-sm font-semibold mb-2">Finance Documents</div>
      {flat.map((doc) => <Row key={doc.id} doc={doc} />)}
      {Object.entries(rvGroups).map(([gid, group]) => (
        <div key={gid} className="mt-1" data-testid={`finance-rv-group-${gid}`}>
          {group.length > 1 && <div className="text-xs text-neutral-500 mt-2 mb-1">Refund Vouchers</div>}
          {group.map((doc) => <Row key={doc.id} doc={doc} />)}
        </div>
      ))}
    </div>
  );
}
